import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAuthState } from "react-firebase-hooks/auth";
import {
  Copy,
  Download,
  RefreshCw,
  Zap,
  PenTool,
  AlignLeft,
  Settings,
  Sparkles,
  CheckCircle,
  AlertCircle,
  FileText,
  Sliders
} from "lucide-react";
import toast, { Toaster } from "react-hot-toast";
import auth from "../../../firebase.init"; 
import { validateGeminiKey } from "../../../utils/geminiUtils";

const ParagraphGenerator = () => {
  // ==========================================
  // 1. STATE MANAGEMENT (Logic Preserved)
  // ==========================================
  const [user, loadingAuth] = useAuthState(auth);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState("");

  
  const credits = "Unlimited";

  // Form State
  const [topic, setTopic] = useState("");
  const [keywords, setKeywords] = useState("");
  const [writingStyle, setWritingStyle] = useState("Informative");
  const [tone, setTone] = useState("Formal");
  const [length, setLength] = useState("Medium");
  const [vocabulary, setVocabulary] = useState("Standard");
  const [numParagraphs, setNumParagraphs] = useState(1);

  const [generatedText, setGeneratedText] = useState("");

  // Configuration Arrays
  const styles = ["Informative", "Creative", "Professional", "Academic", "Marketing"];
  const tones = ["Formal", "Casual", "Friendly", "Persuasive", "Empathetic", "Authoritative"];
  const lengths = ["Short (50-80 words)", "Medium (100-150 words)", "Long (200+ words)"];
  const vocabOptions = ["Simple", "Standard", "Advanced/Technical"];

  // ==========================================
  // 2. BACKEND LOGIC (UNCHANGED)
  // ==========================================

  useEffect(() => {
    const fetchData = async () => {
      if (loadingAuth) return;
      if (!user) {
        setLoading(false);
        return;
      }
      try {
const userResponse = await axios.get(`https://server.enjoywiki.com/youtubeai-may/users`, {
          params: { userEmail: user.email },
        });
        if (userResponse.data && userResponse.data.length > 0) {
          
        }
      } catch (err) {
        setError("Failed to load data");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user, loadingAuth]);  // ==========================================
  // 3. GENERATION LOGIC
  // ==========================================
  const constructPrompt = () => {
    return `You are an expert copywriter. Write content based on:
    Topic: ${topic}
    ${keywords ? `Keywords: ${keywords}` : ""}
    
    Settings:
    - Format: ${numParagraphs} Paragraph(s)
    - Style: ${writingStyle}
    - Tone: ${tone}
    - Length: ${length}
    - Vocabulary: ${vocabulary}

    Rules:
    1. Make it cohesive and clear.
    2. No intro/outro conversational filler.
    3. Return ONLY body text.
    4. Use proper grammar and punctuation.
    Provide the output in plain text format.`;
  };

  const generateParagraphs = async () => {
    if (!topic.trim()) {
      toast.error("Please enter a topic.");
      return;
    }
    

    setGenerating(true);
    setError("");
    setGeneratedText("");

    try {
      // Deduct
      
      
      

      // Generate
            const apiKey = await validateGeminiKey(user.email);
      if (!apiKey) {
        setGenerating(false);
        return;
      }
      const API_URL = `https://generativelanguage.googleapis.com/v1beta/models/${localStorage.getItem("geminiTextModel") || "gemini-2.5-flash-lite"}:generateContent?key=${apiKey}`;
      
      const response = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          contents: [{ role: "user", parts: [{ text: constructPrompt() }] }],
          generationConfig: { temperature: 0.7, maxOutputTokens: 2048 },
        }),
      });

      if (!response.ok) throw new Error("AI Generation Failed");

      const data = await response.json();
      const textResult = data.candidates?.[0]?.content?.parts?.[0]?.text;

      if (textResult) {
        setGeneratedText(textResult);
        toast.success("Content generated!");
      } else {
        throw new Error("Empty response");
      }
    } catch (err) {
      console.error(err);
      toast.error(err.message);
      setError(err.message);
      // Refund
      try {
        
        
      } catch (refundErr) { console.error(refundErr); }
    } finally {
      setGenerating(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(generatedText).then(() => toast.success("Copied!"));
  };

  const downloadText = () => {
    const element = document.createElement("a");
    const file = new Blob([generatedText], { type: "text/plain" });
    element.href = URL.createObjectURL(file);
    element.download = `paragraph-${Date.now()}.txt`;
    document.body.appendChild(element);
    element.click();
  };

  // ==========================================
  // 4. RENDER UI
  // ==========================================

  // Loading State
  if (loadingAuth || loading ) {
    return (
      <div className="loader-wrapper">
        <div className="spinner-border text-primary" role="status"></div>
        <p className="mt-3 text-muted fw-medium">Loading Paragraph Tool...</p>
        <style jsx>{`.loader-wrapper { height: 100vh; display: flex; flex-direction: column; align-items: center; justify-content: center; background: #f8fafc; }`}</style>
      </div>
    );
  }

  // Auth Guard
  if (!user) {
    return (
      <div className="auth-wrapper">
        <div className="auth-card text-center">
          <div className="mb-4 icon-box mx-auto bg-primary bg-opacity-10 text-primary rounded-circle d-flex align-items-center justify-content-center" style={{width: 60, height: 60}}>
            <PenTool size={30} />
          </div>
          <h2 className="fw-bold text-dark mb-2">Paragraph Writer</h2>
          <p className="text-muted mb-4">Log in to generate professional content with AI.</p>
          <button className="btn btn-primary btn-lg w-100 fw-semibold" onClick={() => window.location.href = "/login"}>
            Sign In to Continue
          </button>
        </div>
        <style jsx>{`
          .auth-wrapper { height: 100vh; display: flex; align-items: center; justify-content: center; background: #f1f5f9; }
          .auth-card { background: white; padding: 2.5rem; border-radius: 16px; box-shadow: 0 10px 25px -5px rgba(0,0,0,0.05); max-width: 400px; width: 90%; }
        `}</style>
      </div>
    );
  }

  // Main UI
  return (
    <div className="app-container">
  

      {/* Navbar */}
      <nav className="navbar sticky-top navbar-expand-lg bg-white/80 backdrop-blur border-bottom">
        <div className="container-xl">
          <div className="d-flex align-items-center gap-3">
            <button className="btn btn-icon btn-sm btn-light" onClick={() => window.history.back()}>
              &larr;
            </button>
            <span className="navbar-brand fw-bold d-flex align-items-center gap-2 text-slate-800">
              <div className="brand-icon"><PenTool size={18} color="white" /></div>
              ParagraphAI
            </span>
             <div className="credit-pill">
            <Zap size={14} className="icon-zap" fill="currentColor" />
            <span>Unlimited Credits</span>
            
          </div>
          </div>
          
         
        </div>
      </nav>

      <main className="container-xl py-4 py-lg-5">
        <div className="row g-4">
          
          {/* LEFT: Configuration Panel */}
          <div className="col-lg-4">
            <div className="saas-card h-100">
              <div className="card-header-custom">
                <Sliders size={18} className="text-primary" />
                <h5 className="mb-0 fw-semibold text-slate-700">Settings</h5>
              </div>

              <div className="p-4">
                {/* Topic Input */}
                <div className="mb-4">
                  <label className="form-label-custom">Topic & Context</label>
                  <textarea 
                    className="form-control form-control-custom" 
                    rows="3" 
                    placeholder="Describe what you want to write about..."
                    value={topic}
                    onChange={(e) => setTopic(e.target.value)}
                  />
                </div>

                {/* Keywords Input */}
                <div className="mb-4">
                  <label className="form-label-custom">Keywords <span className="text-muted fw-normal opacity-50">(Optional)</span></label>
                  <div className="input-group-custom">
                    <span className="input-icon"><Sparkles size={16} /></span>
                    <input 
                      type="text"
                      className="form-control form-control-custom ps-5"
                      placeholder="e.g. growth, organic, sustainable"
                      value={keywords}
                      onChange={(e) => setKeywords(e.target.value)}
                    />
                  </div>
                </div>

                <hr className="divider" />

                {/* Dropdowns Grid */}
                <div className="row g-3 mb-4">
                  <div className="col-6">
                    <label className="form-label-custom">Style</label>
                    <select className="form-select form-select-custom" value={writingStyle} onChange={(e) => setWritingStyle(e.target.value)}>
                      {styles.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div className="col-6">
                    <label className="form-label-custom">Tone</label>
                    <select className="form-select form-select-custom" value={tone} onChange={(e) => setTone(e.target.value)}>
                      {tones.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div className="col-6">
                     <label className="form-label-custom">Length</label>
                     <select className="form-select form-select-custom" value={length} onChange={(e) => setLength(e.target.value)}>
                      {lengths.map(l => <option key={l} value={l}>{l.split(' ')[0]}</option>)}
                     </select>
                  </div>
                  <div className="col-6">
                    <label className="form-label-custom">Vocabulary</label>
                    <select className="form-select form-select-custom" value={vocabulary} onChange={(e) => setVocabulary(e.target.value)}>
                      {vocabOptions.map(v => <option key={v} value={v}>{v}</option>)}
                    </select>
                  </div>
                </div>

                {/* Range Slider */}
                <div className="mb-4 p-3 bg-light rounded-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <label className="form-label-custom mb-0">Paragraphs</label>
                    <span className="badge bg-primary rounded-pill px-3">{numParagraphs}</span>
                  </div>
                  <input 
                    type="range" 
                    className="form-range custom-range" 
                    min="1" 
                    max="5" 
                    step="1" 
                    value={numParagraphs}
                    onChange={(e) => setNumParagraphs(e.target.value)}
                  />
                  <div className="d-flex justify-content-between text-muted x-small mt-1">
                    <span>1</span><span>5</span>
                  </div>
                </div>

                {/* Action Button */}
                <button 
                  className="btn btn-primary-gradient w-100"
                  onClick={generateParagraphs}
                  disabled={generating ||  !topic.trim()}
                >
                  {generating ? (
                    <>
                      <RefreshCw className="spin me-2" size={18} /> Generating...
                    </>
                  ) : (
                    <>
                      <Zap size={18} className="me-2" fill="currentColor" /> Generate 
                    </>
                  )}
                </button>
                
                {error && (
                  <div className="mt-3 text-danger small bg-danger bg-opacity-10 p-2 rounded d-flex align-items-center">
                    <AlertCircle size={14} className="me-2" /> {error}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* RIGHT: Output Panel */}
          <div className="col-lg-8">
            <div className="saas-card h-100 d-flex flex-column">
              <div className="card-header-custom justify-content-between">
                <div className="d-flex align-items-center gap-2">
                  <FileText size={18} className="text-primary" />
                  <h5 className="mb-0 fw-semibold text-slate-700">Result</h5>
                </div>
                {generatedText && (
                  <div className="d-flex gap-2">
                    <button className="btn btn-sm btn-outline-custom" onClick={copyToClipboard} title="Copy">
                      <Copy size={16} /> <span className="d-none d-md-inline ms-1">Copy</span>
                    </button>
                    <button className="btn btn-sm btn-outline-custom" onClick={downloadText} title="Download">
                      <Download size={16} />
                    </button>
                  </div>
                )}
              </div>

              <div className="card-body p-0 flex-grow-1 position-relative bg-paper">
                {generating ? (
                  <div className="state-container">
                    <div className="spinner-grow text-primary mb-3" role="status" style={{width: '3rem', height: '3rem'}}></div>
                    <h5 className="text-slate-700">Writing your content...</h5>
                    <p className="text-muted small">Optimizing for {tone} tone and {writingStyle} style.</p>
                  </div>
                ) : generatedText ? (
                  <div className="output-scroll p-4 p-md-5">
                    <div className="document-content">
                      {generatedText.split('\n').map((para, idx) => (
                        para.trim() && <p key={idx}>{para}</p>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="state-container opacity-50">
                    <AlignLeft size={64} className="text-slate-300 mb-3" />
                    <h5 className="text-slate-500">Your content will appear here</h5>
                    <p className="text-muted small">Configure settings on the left to start writing.</p>
                  </div>
                )}
              </div>

              {generatedText && (
                <div className="card-footer-custom">
                  <span className="text-success d-flex align-items-center gap-1 small fw-medium">
                    <CheckCircle size={14} /> Generated Successfully
                  </span>
                  <span className="text-muted small">
                    {generatedText.split(' ').length} words
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* --- INLINE CSS FOR MODERN STYLING --- */}
      <style jsx>{`
        /* VARIABLES & RESET */
        :root {
          --primary-color: #6366f1; /* Indigo */
          --primary-hover: #4f46e5;
          --slate-50: #f8fafc;
          --slate-100: #f1f5f9;
          --slate-200: #e2e8f0;
          --slate-600: #475569;
          --slate-800: #1e293b;
        }

        .app-container {
          background-color: var(--slate-50);
          min-height: 100vh;
          font-family: 'Inter', system-ui, -apple-system, sans-serif;
          color: var(--slate-600);
        }

        /* TEXT UTILS */
        .text-slate-700 { color: #334155; }
        .text-slate-500 { color: #64748b; }
        .x-small { font-size: 0.75rem; }

        /* NAVBAR */
        .backdrop-blur { backdrop-filter: blur(10px); }
        .brand-icon {
          background: var(--primary-color);
          width: 32px; height: 32px;
          border-radius: 8px;
          display: flex; align-items: center; justify-content: center;
        }
        .btn-icon {
          width: 36px; height: 36px;
          display: flex; align-items: center; justify-content: center;
          border-radius: 8px;
          border: 1px solid var(--slate-200);
          color: var(--slate-600);
          transition: all 0.2s;
        }
        .btn-icon:hover { background: var(--slate-100); color: var(--slate-800); }
        
        .credit-pill {
          display: flex; align-items: center; gap: 8px;
          padding: 6px 12px;
          background: #fffbeb; /* Amber-50 */
          color: #b45309; /* Amber-700 */
          border: 1px solid #fcd34d;
          border-radius: 99px;
          font-weight: 600;
          font-size: 0.875rem;
        }
        .buy-btn {
          background: #b45309;
          color: white;
          text-decoration: none;
          padding: 2px 8px;
          border-radius: 4px;
          font-size: 0.7rem;
        }

        /* CARD STYLES */
        .saas-card {
          background: white;
          border: 1px solid var(--slate-200);
          border-radius: 16px;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03);
          overflow: hidden;
          transition: transform 0.2s, box-shadow 0.2s;
        }
        .card-header-custom {
          padding: 1rem 1.5rem;
          border-bottom: 1px solid var(--slate-100);
          background: white;
          display: flex; align-items: center; gap: 0.75rem;
        }
        .card-footer-custom {
          padding: 0.75rem 1.5rem;
          border-top: 1px solid var(--slate-100);
          background: #fcfcfc;
          display: flex; justify-content: space-between; align-items: center;
        }

        /* FORM CONTROLS */
        .form-label-custom {
          font-size: 0.75rem;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          font-weight: 700;
          color: #94a3b8;
          margin-bottom: 0.5rem;
          display: block;
        }
        .form-control-custom, .form-select-custom {
          border: 1px solid var(--slate-200);
          border-radius: 10px;
          padding: 0.65rem 0.85rem;
          font-size: 0.95rem;
          background-color: #fff;
          color: var(--slate-800);
          transition: all 0.2s;
        }
        .form-control-custom:focus, .form-select-custom:focus {
          border-color: var(--primary-color);
          box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.15);
          outline: none;
        }
        
        .input-group-custom { position: relative; }
        .input-icon {
          position: absolute; left: 12px; top: 50%; transform: translateY(-50%);
          color: #94a3b8; pointer-events: none; z-index: 10;
        }

        .divider { border-color: var(--slate-100); margin: 1.5rem 0; }

        /* BUTTONS */
        .btn-primary-gradient {
          background: linear-gradient(135deg, var(--primary-color) 0%, var(--primary-hover) 100%);
          color: white;
          border: none;
          padding: 0.85rem;
          border-radius: 10px;
          font-weight: 600;
          letter-spacing: 0.01em;
          box-shadow: 0 4px 12px rgba(99, 102, 241, 0.25);
          transition: all 0.2s;
        }
        .btn-primary-gradient:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(99, 102, 241, 0.35);
        }
        .btn-primary-gradient:disabled {
          background: #cbd5e1;
          box-shadow: none;
          cursor: not-allowed;
        }
        
        .btn-outline-custom {
          border: 1px solid var(--slate-200);
          color: var(--slate-600);
          border-radius: 8px;
          display: flex; align-items: center;
          transition: all 0.2s;
        }
        .btn-outline-custom:hover {
          background: var(--slate-50);
          color: var(--slate-800);
          border-color: #cbd5e1;
        }

        /* OUTPUT AREA */
        .bg-paper {
          background-color: #ffffff;
          background-image: radial-gradient(#f1f5f9 1px, transparent 1px);
          background-size: 20px 20px;
        }
        .output-scroll {
          height: 100%;
          overflow-y: auto;
          min-height: 400px;
        }
        .document-content {
          font-family: 'Georgia', serif; /* Serif for better reading experience */
          font-size: 1.05rem;
          line-height: 1.8;
          color: #1e293b;
          max-width: 65ch; /* Optimal reading width */
          margin: 0 auto;
        }
        .document-content p { margin-bottom: 1.5rem; }
        
        .state-container {
          display: flex; flex-direction: column; align-items: center; justify-content: center;
          height: 100%; min-height: 400px;
          text-align: center;
          padding: 2rem;
        }

        .spin { animation: spin 1s linear infinite; }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};

export default ParagraphGenerator;